import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import { signalsData } from "Stores/signalsData";
import ContactAvatar from "Pages/Dashboard/contacts/ContactAvatar";
import { AddContactsToList } from "Pages/Dashboard/contacts/AddContactsToList";
import { encodeListBuilderImport } from "Pages/Dashboard/agents/icpListBuilder";
import SignalIcon from "./SignalIcon";
import { addToList, addPersonToList, ensureContactLists, findEmailFromLinkedIn, findPhoneFromLinkedIn, reviewDraft } from "./feedActions";
import { greetByName, offDomain } from "./signalMeta";

/**
 * "Also reach: <persona>": the people with that persona at the account, in a small card under the link. Hovering
 * the link a moment starts the lookup, so the card usually opens already filled. Each person can be opened (when
 * they are a contact), added to a list, or looked up for an email or phone, at the costs the buttons say.
 */

export const HOVER_MS = 250;
const GAP = 6;

export const listBuilderLink = (domain, persona) =>
  `/list-builder?sgLb=${encodeListBuilderImport({ filters: { companyDomain: [domain], companySearchType: "domain", ...(persona ? { title: [persona] } : {}) } })}`;

const Skeleton = () => (
  <div className="sg-pcard sg-pcard--skel" aria-hidden="true">
    <span className="sg-pcard__skav" />
    <span className="sg-pcard__sklines"><i /><i /></span>
  </div>
);

const Person = ({ p, query, company, onAddToList, onDone, draft, onDrafted, disabled = false }) => {
  const history = useHistory();
  const [busyState, setBusy] = useState("");
  const busy = disabled ? "all" : busyState;
  const asked = { ...p, company, domain: query.domain };
  const run = (kind, fn) => async () => {
    setBusy(kind);
    try {
      const hit = await fn(asked);
      if (hit && hit.contactId) signalsData.markPersonContact(query, p.linkedinUrl, hit.contactId, hit.email);
    } finally {
      setBusy("");
    }
  };
  // With a suggested draft: open it to this person (their saved email, else a 1 credit lookup that saves them),
  // greeting them by name. The card closes once the composer opens, so busy is only cleared when it didn't.
  const useDraft = async () => {
    setBusy("draft");
    const named = { ...draft, body: greetByName(draft.body, p.name) };
    const sent = await reviewDraft(named, { ...asked, email: "", contactId: p.alreadyContactId || null }, history);
    if (!sent) {
      setBusy("");
      return;
    }
    if (sent.contactId) signalsData.markPersonContact(query, p.linkedinUrl, sent.contactId, sent.email);
    onDone();
    if (onDrafted) onDrafted(sent);
  };
  const openContact = () => {
    onDone();
    history.push(`/contacts/${p.alreadyContactId}/activity`);
  };
  return (
    <div className="sg-pcard">
      <ContactAvatar photo={p.photoUrl} name={p.name} />
      <div className="sg-pcard__text">
        <div className="sg-pcard__name">
          <span>{p.name}</span>
          {p.linkedinUrl && (
            <a className="sg-pcard__li" href={p.linkedinUrl} target="_blank" rel="noopener noreferrer" title="LinkedIn profile" aria-label={`${p.name} on LinkedIn`}>
              <SignalIcon id="linkedin" size={13} />
            </a>
          )}
        </div>
        {p.title && <div className="sg-pcard__title">{p.title}</div>}
        {p.location && <div className="sg-pcard__loc">{p.location}</div>}
        {offDomain(p.email, query.domain) && <div className="sg-pcard__warn" title={p.email}>Email domain differs</div>}
        <div className="sg-pcard__acts">
          {draft && p.alreadyContactId ? (
            <>
              <button className="sg-btn sg-btn--small" disabled={!!busy} onClick={useDraft}>{busy === "draft" ? "Opening…" : "Send draft"}</button>
              <button className="sg-btn sg-btn--small sg-btn--ghost" onClick={() => onAddToList(p)}>Add to list</button>
            </>
          ) : draft ? (
            <>
              <button className="sg-btn sg-btn--small" disabled={!!busy || !p.linkedinUrl} onClick={useDraft}>
                {busy === "draft" ? "Finding email…" : "Find email & send · 1 credit"}
              </button>
              <button className="sg-btn sg-btn--small sg-btn--ghost" disabled={!!busy || !p.linkedinUrl} onClick={() => onAddToList(p)}>Add to list</button>
            </>
          ) : p.alreadyContactId ? (
            <>
              <button className="sg-btn sg-btn--small" onClick={openContact}>Open contact</button>
              <button className="sg-btn sg-btn--small sg-btn--ghost" onClick={() => onAddToList(p)}>Add to list</button>
            </>
          ) : (
            <>
              <button className="sg-btn sg-btn--small sg-btn--ghost" disabled={!!busy || !p.linkedinUrl} onClick={() => onAddToList(p)}>Add to list</button>
              <button className="sg-btn sg-btn--small sg-btn--ghost" disabled={!!busy || !p.linkedinUrl} onClick={run("email", findEmailFromLinkedIn)}>
                {busy === "email" ? "Finding…" : "Find email · 1 credit"}
              </button>
              <button className="sg-btn sg-btn--small sg-btn--ghost" disabled={!!busy || !p.linkedinUrl} onClick={run("phone", findPhoneFromLinkedIn)}>
                {busy === "phone" ? "Finding…" : "Find phone · 10 credits"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/** The card itself: portalled to the body under its link, closed by Escape or a click outside. */
const LOOKING = ["Looking them up…", "Checking who works there now…", "Picking the closest match…", "Almost there…"];

const PeoplePopover = observer(({ anchor, query, company, onClose, onAddToList, draft, onDrafted, alignRight = false }) => {
  const history = useHistory();
  const ref = useRef(null);
  // Fixed to the viewport, so it follows the link when the page (or the feed's scroller) moves.
  useLayoutEffect(() => {
    const place = () => {
      const el = ref.current;
      if (!el || !anchor) return;
      const r = anchor.getBoundingClientRect();
      const wanted = alignRight ? r.right - el.offsetWidth : r.left;
      const left = Math.max(8, Math.min(wanted, window.innerWidth - el.offsetWidth - 8));
      el.style.setProperty("--sg-pop-top", `${r.bottom + GAP}px`);
      el.style.setProperty("--sg-pop-left", `${left}px`);
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [anchor, alignRight]);
  useEffect(() => {
    const onKey = e => e.key === "Escape" && onClose();
    const onDown = e => {
      if (ref.current && !ref.current.contains(e.target) && !(anchor && anchor.contains(e.target))) onClose();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [anchor, onClose]);

  const entry = signalsData.peopleFor(query);
  const data = entry && entry.data;
  const people = (data && data.people) || [];
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (data) return undefined;
    const t = setInterval(() => setTick(n => n + 1), 1800);
    return () => clearInterval(t);
  }, [data]);
  // "Find all": each person's email in turn (1 credit each, only when found), so every button becomes Send.
  const [findingAll, setFindingAll] = useState(false);
  const unresolved = people.filter(p => !p.alreadyContactId && p.linkedinUrl);
  const findAll = async () => {
    setFindingAll(true);
    try {
      for (const p of unresolved) {
        const hit = await findEmailFromLinkedIn({ ...p, company, domain: query.domain }); // eslint-disable-line no-await-in-loop
        if (hit && hit.contactId) signalsData.markPersonContact(query, p.linkedinUrl, hit.contactId, hit.email);
      }
    } finally {
      setFindingAll(false);
    }
  };
  let body;
  if (!data) {
    body = (
      <>
        <div className="sg-whopop__status" role="status" aria-live="polite">
          <span className="sg-draft__spin" aria-hidden="true" />{LOOKING[Math.min(tick, LOOKING.length - 1)]}
        </div>
        <Skeleton /><Skeleton /><Skeleton />
      </>
    );
  }
  else if (data.reason === "unavailable") {
    body = (
      <div className="sg-whopop__empty">
        <b>Couldn't look them up right now.</b>
        <button className="sg-btn sg-btn--small sg-btn--ghost" onClick={() => signalsData.fetchPeople(query)}>Try again</button>
      </div>
    );
  } else if (!people.length) {
    body = (
      <div className="sg-whopop__empty">
        <b>No {query.persona} found at {company || "this account"}</b>
        {query.domain && (
          <button className="sg-btn sg-btn--small sg-btn--ghost" onClick={() => { onClose(); history.push(listBuilderLink(query.domain, query.persona)); }}>
            Search in List Builder
          </button>
        )}
      </div>
    );
  } else {
    body = (
      <>
        {people.map(p => (
          <Person key={p.linkedinUrl || p.entityUrn || p.name} p={p} query={query} company={company} onAddToList={onAddToList} onDone={onClose} draft={draft} onDrafted={onDrafted} disabled={findingAll} />
        ))}
        {draft && unresolved.length > 1 && (
          <div className="sg-whopop__all">
            <button className="sg-btn sg-btn--small sg-btn--ghost" disabled={findingAll} onClick={findAll}>
              {findingAll ? "Finding emails…" : `Find all emails · ${unresolved.length} credits`}
            </button>
            <small>1 credit each, only when an email is found. Then Send is free.</small>
          </div>
        )}
      </>
    );
  }

  return ReactDOM.createPortal(
    <div ref={ref} className="sg-whopop" role="dialog" aria-label={`${query.persona} at ${company || "this account"}`}>
      <div className="sg-whopop__head">{draft ? "Send to: " : ""}{query.persona}{company ? ` at ${company}` : ""}</div>
      <div className="sg-whopop__body" aria-busy={!data}>{body}</div>
    </div>,
    document.body
  );
});

/**
 * The persona as a link ("Also reach: VP Data"), or as the row's main button ("Find VP Data") when the post came
 * from a recruiter and the persona is who to reach. Clicking opens the card; it closes before the list picker opens.
 */
export const AlsoReach = ({ persona, domain, accountId, accountName, asButton = false, label = "", draft = null, onDrafted }) => {
  const [open, setOpen] = useState(false);
  const [listFor, setListFor] = useState(null);
  const [listTrigger, setListTrigger] = useState(0);
  const anchor = useRef(null);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  // Looked up once the link scrolls into view, so the card usually opens already filled (one cached search per
  // account and persona for a week; no credits).
  useEffect(() => {
    const el = anchor.current;
    if (!persona || !(domain || accountId) || !el || typeof IntersectionObserver === "undefined") return undefined;
    const seen = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        signalsData.fetchPeople({ domain, persona, accountId });
        seen.disconnect();
      }
    });
    seen.observe(el);
    return () => seen.disconnect();
  }, [persona, domain, accountId]);
  if (!persona) return null;
  const query = { domain, persona, accountId };
  const canLook = !!(domain || accountId);

  const prefetch = () => {
    if (!canLook) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => signalsData.fetchPeople(query), HOVER_MS);
  };
  const cancel = () => clearTimeout(timer.current);
  const toggle = () => {
    cancel();
    if (!open) signalsData.fetchPeople(query);
    setOpen(!open);
  };
  const pickList = async p => {
    setOpen(false);
    await ensureContactLists();
    setListFor(p);
    setListTrigger(n => n + 1);
  };
  const addPicked = async (type, listId, close = () => {}) => {
    if (listId && listFor) {
      if (listFor.alreadyContactId) await addToList(listFor.alreadyContactId, listId);
      else await addPersonToList(listFor, listId, { company: accountName, domain });
    }
    close();
  };

  const trigger = asButton ? (
    <button ref={anchor} className="sg-btn sg-btn--small" aria-haspopup="dialog" aria-expanded={open} disabled={!canLook} onClick={toggle} onMouseEnter={prefetch} onMouseLeave={cancel}>
      {draft ? "Send draft to…" : `Find ${persona}`}
    </button>
  ) : label ? (
    <button ref={anchor} className="sg-halso__btn" aria-haspopup="dialog" aria-expanded={open} disabled={!canLook} onClick={toggle} onMouseEnter={prefetch} onMouseLeave={cancel} onFocus={prefetch}>
      {label}
    </button>
  ) : (
    <span className="sg-halso">
      Also reach:{" "}
      {canLook ? (
        <button ref={anchor} className="sg-halso__btn" aria-haspopup="dialog" aria-expanded={open} onClick={toggle} onMouseEnter={prefetch} onMouseLeave={cancel} onFocus={prefetch}>
          {persona}
        </button>
      ) : <b>{persona}</b>}
    </span>
  );
  return (
    <>
      {trigger}
      {open && (
        <PeoplePopover anchor={anchor.current} query={query} company={accountName} onClose={() => setOpen(false)} onAddToList={pickList} draft={draft} onDrafted={onDrafted} alignRight={asButton} />
      )}
      {listFor && (
        <AddContactsToList hidden contactIds={[listFor.alreadyContactId || listFor.linkedinUrl]} trigger={listTrigger} addToListCbk={addPicked} />
      )}
    </>
  );
};
